import { authFetchBlob } from './authFetch';

const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/** One export download: the gateway path to fetch and the name the file saves under. */
export interface ExportDownload {
  /** Identifies the download for in-flight sharing — one request per key. */
  key: string;
  path: string;
  filename: string;
}

// One in-flight request per key. Re-mounts of the download modal (StrictMode's
// mount → unmount → mount, or the user closing and reopening it mid-fetch) join the
// running promise instead of firing a duplicate request — and nothing aborts it, so
// closing the modal never cancels the download. Settled entries are evicted so a
// retry starts a fresh request.
const inFlightByKey = new Map<string, Promise<void>>();

// In-flight changes are observable so UI outside the modal (e.g. the Download
// button) can react — the map is module state, invisible to React on its own.
const inFlightListeners = new Set<() => void>();
const notifyInFlightChange = (): void => inFlightListeners.forEach((listener) => listener());

/** Subscribe to in-flight download changes; returns the unsubscribe function. */
export const subscribeToExportDownloadsInFlight = (listener: () => void): (() => void) => {
  inFlightListeners.add(listener);
  return () => inFlightListeners.delete(listener);
};

/** Whether the export download with this key is currently in flight. */
export const isExportDownloadInFlight = (key: string): boolean => inFlightByKey.has(key);

/**
 * Fetch an export file the gateway builds on the fly and save it via a synthetic
 * anchor click.
 *
 * Deliberately a plain async function, NOT a react-query mutation: the caller
 * fires it from an on-mount effect, and a mutation observer detaches from its
 * in-flight mutation during StrictMode's unsubscribe/re-subscribe and never
 * re-attaches — so the file would download but neither the observer state nor
 * its callbacks would ever report success. Concurrent callers for the same key
 * share the in-flight promise, so the file saves exactly once.
 */
export const downloadExport = ({ key, path, filename }: ExportDownload): Promise<void> => {
  const existing = inFlightByKey.get(key);
  if (existing) return existing;

  const request = (async () => {
    const blob = await authFetchBlob(path);
    triggerBrowserDownload(blob, filename);
  })().finally(() => {
    inFlightByKey.delete(key);
    notifyInFlightChange();
  });

  inFlightByKey.set(key, request);
  notifyInFlightChange();
  return request;
};
